import React, { useState, useEffect, Component, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import api, { API_URL } from './services/api';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { identifyUser, resetUser, trackEvent } from './services/analytics';

// Eagerly loaded components (public routes / entry points)
import LoginPage from './components/LoginPage';
import ConfirmEmailChange from './pages/ConfirmEmailChange';
import PricingPage from './components/PricingPage';
import PaymentPage from './components/PaymentPage';
import PaymentSuccess from './components/PaymentSuccess';
import ProfileSetup from './components/ProfileSetup';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import RefundPolicy from './components/RefundPolicy';
import PublicSurvey from './components/PublicSurvey';
import PublicFunnelPage from './components/PublicFunnelPage';
import PublicWebForm from './components/PublicWebForm';
import OAuthCallback from './components/OAuthCallback';
import LearnPaymentPage from './components/LearnPaymentPage';
import LearnThankYou from './components/LearnThankYou';
import StudentLogin from './components/StudentLogin';
import CourseViewer from './components/CourseViewer';
import SetPasswordPage from './components/SetPasswordPage';
import TrialExpiredPage from './components/TrialExpiredPage';
import { PageLoader } from './components/ui/loader';

// Retry wrapper for lazy imports — auto-reloads on stale chunks after deploy
const lazyRetry = (fn) => lazy(() => fn().catch(() => {
  const reloaded = sessionStorage.getItem('lazy_reload');
  if (!reloaded) {
    sessionStorage.setItem('lazy_reload', '1');
    window.location.reload();
    return new Promise(() => {});
  }
  sessionStorage.removeItem('lazy_reload');
  return fn();
}));

// Lazy-loaded components (protected routes — code-split per route, with retry)
const Dashboard = lazyRetry(() => import('./components/Dashboard'));
const DocsPage = lazyRetry(() => import('./components/DocsPage'));
const GTMDashboard = lazyRetry(() => import('./components/GTMDashboard'));
const MPOEngine = lazyRetry(() => import('./components/MPOEngine'));
const MPOStrategy = lazyRetry(() => import('./components/MPOStrategy'));
const GTMProject = lazyRetry(() => import('./components/GTMProject'));
const KanbanBoard = lazyRetry(() => import('./components/KanbanBoard'));
const ContactDetails = lazyRetry(() => import('./components/ContactDetails'));
const CompanyDetails = lazyRetry(() => import('./components/CompanyDetails'));
const MarketingOps = lazyRetry(() => import('./components/MarketingOps'));
const SupportCRM = lazyRetry(() => import('./components/SupportCRM'));
const FieldManager = lazyRetry(() => import('./components/FieldManager'));
const UserManagement = lazyRetry(() => import('./components/UserManagement'));
const MyProfile = lazyRetry(() => import('./components/MyProfile'));
const Settings = lazyRetry(() => import('./components/Settings'));
const PipelineSettings = lazyRetry(() => import('./components/PipelineSettings'));
const MarketingWorkflowSettings = lazyRetry(() => import('./components/MarketingWorkflowSettings'));
const SupportSettings = lazyRetry(() => import('./components/SupportSettings'));
const Research = lazyRetry(() => import('./components/Research'));
const WhatsAppAutomation = lazyRetry(() => import('./components/WhatsAppAutomation'));
const SocialMediaAutomation = lazyRetry(() => import('./components/SocialMediaAutomation'));
const EmailMarketing = lazyRetry(() => import('./components/EmailMarketing'));
const WebForms = lazyRetry(() => import('./components/WebForms'));
const WebFunnels = lazyRetry(() => import('./components/WebFunnels'));
const ContactsPage = lazyRetry(() => import('./components/ContactsPage'));
const TagsPage = lazyRetry(() => import('./components/TagsPage'));
const SegmentsPage = lazyRetry(() => import('./components/SegmentsPage'));
const SurveyDashboard = lazyRetry(() => import('./components/SurveyDashboard'));
const SurveyBuilder = lazyRetry(() => import('./components/SurveyBuilder'));
const SurveyResponses = lazyRetry(() => import('./components/SurveyResponses'));
const OptimizationAnalyzer = lazyRetry(() => import('./components/OptimizationAnalyzer'));
const OptimizationCrawlSite = lazyRetry(() => import('./components/OptimizationCrawlSite'));
const CompaniesPage = lazyRetry(() => import('./components/CompaniesPage'));
const TasksPage = lazyRetry(() => import('./components/TasksPage'));
const AffiliateDashboard = lazyRetry(() => import('./components/AffiliateDashboard'));
const CEODashboard = lazyRetry(() => import('./components/CEODashboard'));
const LMSDashboard = lazyRetry(() => import('./components/LMSDashboard'));
const CourseBuilder = lazyRetry(() => import('./components/CourseBuilder'));
const LMSStudents = lazyRetry(() => import('./components/LMSStudents'));
const LMSSettings = lazyRetry(() => import('./components/LMSSettings'));
const IntegrationsPage = lazyRetry(() => import('./components/IntegrationsPage'));
const AISettingsPage = lazyRetry(() => import('./components/AISettingsPage'));

// Detects custom domain and renders funnel, otherwise falls through
const isCustomHost = () => {
  const h = window.location.hostname;
  return h !== 'app.salesqualifyd.com' && !h.includes('preview.emergentagent') && !h.includes('localhost') && !h.endsWith('.emergent.host');
};

// Root of a customer's custom domain: send visitors to that workspace's LMS portal or latest funnel
function CustomDomainRoot() {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    fetch(`${API_URL}/public/domain-resolve/${window.location.hostname}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setState({ loading: false, d }))
      .catch(() => setState({ loading: false, d: null }));
  }, []);
  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;
  if (state.d?.type === 'lms') return <Navigate to={`/${state.d.organization_id}/courses`} replace />;
  if (state.d?.type === 'funnel' && state.d.default_funnel_slug) return <Navigate to={`/${state.d.default_funnel_slug}`} replace />;
  return <div className="min-h-screen flex items-center justify-center text-gray-500" data-testid="custom-domain-not-connected">This domain is not connected yet.</div>;
}

// Clean student links on a school's custom domain (e.g. learn.school.com/student/set-password?token=…)
function CustomDomainStudentPath() {
  const loc = useLocation();
  const [org, setOrg] = useState(undefined);
  useEffect(() => {
    fetch(`${API_URL}/public/domain-resolve/${window.location.hostname}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setOrg(d.organization_id || null))
      .catch(() => setOrg(null));
  }, []);
  if (org === undefined) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;
  if (!org) return <div className="min-h-screen flex items-center justify-center text-gray-500" data-testid="custom-domain-not-connected">This domain is not connected yet.</div>;
  return <Navigate to={`/${org}${loc.pathname}${loc.search}`} replace />;
}

function CustomDomainOrRedirect() {
  const hostname = window.location.hostname;
  const isCustomDomain = hostname !== 'app.salesqualifyd.com' && 
                         !hostname.includes('preview.emergentagent') && 
                         !hostname.includes('localhost');
  if (isCustomDomain) return <PublicFunnelPage />;
  const token = localStorage.getItem('token');
  if (token) return <Navigate to="/" replace />;
  return <Navigate to="/login" replace />;
}



// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [showWorkspaceSelector, setShowWorkspaceSelector] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get(`${API_URL}/auth/me`, { timeout: 15000 });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout on explicit 401 (invalid token), NOT on network errors
      if (error.response?.status === 401) {
        logout();
      }
      // Network error — keep the token, user might just have connectivity issues
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData, workspaces: userWorkspaces, requires_workspace_selection } = response.data;
      
      // If user has multiple workspaces, show selector
      if (requires_workspace_selection && userWorkspaces && userWorkspaces.length > 1) {
        setPendingLoginData({ access_token, user: userData });
        setWorkspaces(userWorkspaces);
        setShowWorkspaceSelector(true);
        return { success: true, requiresWorkspaceSelection: true };
      }
      
      // Single workspace - proceed normally
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      setWorkspaces(userWorkspaces || []);
      // Track user login with Mixpanel
      identifyUser(userData);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      trackEvent('Login Failed', { error: message });
      return { success: false, message };
    }
  };

  const selectWorkspace = async (workspace) => {
    if (!pendingLoginData) return;
    
    try {
      // Set initial token
      // If selecting a different workspace than current, switch to it
      if (workspace.id !== pendingLoginData.user.organization_id) {
        const response = await api.post(`${API_URL}/workspaces/switch`, {
          organization_id: workspace.id
        });
        
        const { access_token, user: updatedUser } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(updatedUser);

      } else {
        // Use the pending login data directly
        localStorage.setItem('token', pendingLoginData.access_token);
        setToken(pendingLoginData.access_token);
        setUser(pendingLoginData.user);
      }
      
      setShowWorkspaceSelector(false);
      setPendingLoginData(null);
      toast.success(`Switched to ${workspace.name}`);
    } catch (error) {
      console.error('Failed to select workspace:', error);
      toast.error('Failed to select workspace');
    }
  };

  const cancelWorkspaceSelection = () => {
    setShowWorkspaceSelector(false);
    setPendingLoginData(null);
    setWorkspaces([]);
  };

  const register = async (email, password, fullName) => {
    try {
      const response = await api.post(`${API_URL}/auth/register`, {
        email,
        password,
        full_name: fullName
      });
      
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    // Track logout before resetting
    resetUser();
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setWorkspaces([]);
    setShowWorkspaceSelector(false);
    setPendingLoginData(null);

    toast.info('Logged out successfully');
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    workspaces,
    showWorkspaceSelector,
    selectWorkspace,
    cancelWorkspaceSelection
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader label="Loading your workspace…" />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // REMOVED: Frontend payment check - backend verify_paid_user handles this
  // This allows invited users (payment_status='unpaid') to access if their org has a paying admin
  
  return children;
};

// Error Boundary — auto-recovers from ChunkLoadError (stale cache after deploy)
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    // Auto-reload on ChunkLoadError (stale chunks after deploy)
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      const reloadKey = 'chunk_reload_' + window.location.pathname;
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      // Only auto-reload once per 30 seconds to prevent infinite loop
      if (!lastReload || (now - parseInt(lastReload)) > 30000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
        return;
      }
    }
  }

  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error?.name === 'ChunkLoadError' || 
                           this.state.error?.message?.includes('Loading chunk');
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '2rem', textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            {isChunkError ? 'New version available' : 'Something went wrong'}
          </h1>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
            {isChunkError 
              ? 'A new version has been deployed. Click below to load it.'
              : 'An unexpected error occurred. Please refresh the page to try again.'}
          </p>
          {!isChunkError && this.state.error && (
            <details style={{ marginBottom: '1rem', textAlign: 'left', maxWidth: '600px', width: '100%' }}>
              <summary style={{ cursor: 'pointer', color: '#9ca3af', fontSize: '0.75rem' }}>Error details</summary>
              <pre style={{ fontSize: '0.7rem', color: '#ef4444', background: '#fef2f2', padding: '0.75rem', borderRadius: '0.375rem', overflow: 'auto', marginTop: '0.5rem' }}>
                {this.state.error.toString()}
              </pre>
            </details>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '0.5rem 1.5rem', backgroundColor: '#f97316', color: 'white', borderRadius: '0.375rem', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            {isChunkError ? 'Load New Version' : 'Refresh Page'}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Main App Component
function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/setup-profile" element={<ProfileSetup />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<LoginPage />} />
            <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/trial-expired" element={<TrialExpiredPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/affiliate" element={<AffiliateDashboard />} />
            <Route path="/ceo" element={<CEODashboard />} />
            
            {/* Learner Payment Routes (Hidden/NoIndex) */}
            <Route path="/learn" element={<LearnPaymentPage />} />
            <Route path="/learn/thank-you" element={<LearnThankYou />} />

            {/* Public Funnel Pages — no auth required */}
            <Route path="/f/:workspaceId/:funnelSlug" element={<PublicFunnelPage />} />
            <Route path="/f/:workspaceId/:funnelSlug/:pageSlug" element={<PublicFunnelPage />} />
            
            {/* Public Web Form — no auth required (for iframe embed) */}
            <Route path="/:orgId/form/:formId" element={<PublicWebForm />} />
            
            {/* LMS Public Routes — Student login & course viewer */}
            <Route path="/:orgId/courses/:courseSlug/login" element={<StudentLogin />} />
            <Route path="/:orgId/courses/:courseSlug" element={<CourseViewer />} />
            <Route path="/:orgId/courses/:courseSlug/:moduleSlug/:chapterSlug" element={<CourseViewer />} />
            <Route path="/:orgId/courses" element={<CourseViewer />} />
            <Route path="/:orgId/student/set-password" element={<StudentLogin />} />
            <Route path="/:orgId/student/magic-login" element={<StudentLogin />} />
            <Route path="/student/set-password" element={<CustomDomainStudentPath />} />
            <Route path="/student/magic-login" element={<CustomDomainStudentPath />} />
            <Route path="/courses/:courseSlug/login" element={<CustomDomainStudentPath />} />
            <Route path="/courses/:courseSlug" element={<CustomDomainStudentPath />} />
            <Route path="/courses" element={<CustomDomainStudentPath />} />
            
            {/* Protected Routes */}
            <Route path="/" element={
              isCustomHost() ? <CustomDomainRoot /> : (
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>)
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/kanban" element={
              <ProtectedRoute>
                <KanbanBoard />
              </ProtectedRoute>
            } />
            <Route path="/contacts" element={
              <ProtectedRoute>
                <ContactsPage />
              </ProtectedRoute>
            } />
            <Route path="/tags" element={
              <ProtectedRoute>
                <TagsPage />
              </ProtectedRoute>
            } />
            <Route path="/segments" element={
              <ProtectedRoute>
                <SegmentsPage />
              </ProtectedRoute>
            } />
            <Route path="/marketing" element={
              <ProtectedRoute>
                <MarketingOps />
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute>
                <SupportCRM />
              </ProtectedRoute>
            } />
            <Route path="/research" element={
              <ProtectedRoute>
                <Research />
              </ProtectedRoute>
            } />
            {/* Optimization - Standalone with two separate pages */}
            <Route path="/optimization/analyzer" element={
              <ProtectedRoute>
                <OptimizationAnalyzer />
              </ProtectedRoute>
            } />
            <Route path="/optimization/crawl" element={
              <ProtectedRoute>
                <OptimizationCrawlSite />
              </ProtectedRoute>
            } />
            {/* GTM Builder Routes */}
            <Route path="/mpo" element={
              <ProtectedRoute>
                <MPOEngine />
              </ProtectedRoute>
            } />
            <Route path="/mpo/strategy" element={<Navigate to="/mpo?tab=strategy" replace />} />
            <Route path="/gtm" element={
              <ProtectedRoute>
                <GTMDashboard />
              </ProtectedRoute>
            } />
            <Route path="/gtm/projects" element={
              <ProtectedRoute>
                <GTMDashboard />
              </ProtectedRoute>
            } />
            <Route path="/gtm/:projectId" element={
              <ProtectedRoute>
                <GTMProject />
              </ProtectedRoute>
            } />
            <Route path="/admin/fields" element={
              <ProtectedRoute>
                <FieldManager />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <MyProfile />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/admin/pipeline" element={
              <ProtectedRoute>
                <PipelineSettings />
              </ProtectedRoute>
            } />
            <Route path="/settings/pipeline" element={
              <ProtectedRoute>
                <PipelineSettings />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/workflow" element={
              <ProtectedRoute>
                <MarketingWorkflowSettings />
              </ProtectedRoute>
            } />
            <Route path="/admin/support/settings" element={
              <ProtectedRoute>
                <SupportSettings />
              </ProtectedRoute>
            } />
            <Route path="/automation/whatsapp" element={
              <ProtectedRoute>
                <WhatsAppAutomation />
              </ProtectedRoute>
            } />
            <Route path="/automation/social" element={
              <ProtectedRoute>
                <SocialMediaAutomation />
              </ProtectedRoute>
            } />
            <Route path="/automation/email" element={
              <ProtectedRoute>
                <EmailMarketing />
              </ProtectedRoute>
            } />
            <Route path="/automation/webforms" element={
              <ProtectedRoute>
                <WebForms />
              </ProtectedRoute>
            } />
            <Route path="/automation/funnels" element={
              <ProtectedRoute>
                <WebFunnels />
              </ProtectedRoute>
            } />
            <Route path="/contact/:id" element={
              <ProtectedRoute>
                <ContactDetails />
              </ProtectedRoute>
            } />
            <Route path="/companies" element={
              <ProtectedRoute>
                <CompaniesPage />
              </ProtectedRoute>
            } />
            <Route path="/company/:id" element={
              <ProtectedRoute>
                <CompanyDetails />
              </ProtectedRoute>
            } />
            <Route path="/tasks" element={
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            } />
            {/* LMS Admin Routes */}
            <Route path="/lms" element={
              <ProtectedRoute>
                <LMSDashboard />
              </ProtectedRoute>
            } />
            <Route path="/lms/students" element={
              <ProtectedRoute>
                <LMSStudents />
              </ProtectedRoute>
            } />
            <Route path="/lms/settings" element={
              <ProtectedRoute>
                <LMSSettings />
              </ProtectedRoute>
            } />
            <Route path="/lms/:courseId" element={
              <ProtectedRoute>
                <CourseBuilder />
              </ProtectedRoute>
            } />
            <Route path="/admin/integrations" element={
              <ProtectedRoute>
                <IntegrationsPage />
              </ProtectedRoute>
            } />
            <Route path="/settings/ai" element={
              <ProtectedRoute>
                <AISettingsPage />
              </ProtectedRoute>
            } />
            {/* Survey Routes */}
            <Route path="/surveys" element={
              <ProtectedRoute>
                <SurveyDashboard />
              </ProtectedRoute>
            } />
            <Route path="/surveys/new" element={
              <ProtectedRoute>
                <SurveyBuilder />
              </ProtectedRoute>
            } />
            <Route path="/surveys/:surveyId/edit" element={
              <ProtectedRoute>
                <SurveyBuilder />
              </ProtectedRoute>
            } />
            <Route path="/surveys/:surveyId/responses" element={
              <ProtectedRoute>
                <SurveyResponses />
              </ProtectedRoute>
            } />
            <Route path="/surveys/:surveyId/analytics" element={
              <ProtectedRoute>
                <SurveyResponses />
              </ProtectedRoute>
            } />
            {/* Public Survey Route (no auth required) */}
            <Route path="/s/:surveyId" element={<PublicSurvey />} />
            <Route path="/auth/linkedin/callback" element={<OAuthCallback />} />
            <Route path="/auth/instagram/callback" element={<OAuthCallback />} />
            {/* Custom domain funnel catch — if on a non-platform domain, any path is a funnel */}
            <Route path="/:funnelSlug" element={<CustomDomainOrRedirect />} />
            {/* Catch all route - redirect to dashboard */}
            <Route path="*" element={
              <ProtectedRoute>
                <Navigate to="/" replace />
              </ProtectedRoute>
            } />
          </Routes>
          </Suspense>
        </Router>
        <Toaster />
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;